import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Ensure ScrollTrigger is registered
gsap.registerPlugin(ScrollTrigger);

interface AnimationTarget {
  current: HTMLElement | null;
}

/**
 * Optimized footer entrance animation with better performance
 * @param footerRef - Reference to the footer element
 */
export const animateFooterEnter = (footerRef: AnimationTarget) => {
  if (!footerRef.current) {
    console.warn("Footer ref is null");
    return;
  }

  const footerElements = footerRef.current.querySelectorAll(".footer-animated");

  if (!footerElements.length) {
    console.warn("No footer-animated elements found");
    return;
  }

  console.log(`Found ${footerElements.length} footer elements to animate`);

  // Create timeline for better control
  const tl = gsap.timeline({
    paused: true,
    onStart: () => console.log("Footer animation started"),
    onComplete: () => console.log("Footer animation completed"),
  });

  // Set initial state - make sure elements are visible first
  gsap.set(footerElements, {
    opacity: 1, // Start visible to avoid flash
    y: 0,
    scale: 1,
  });

  // Then animate from hidden state
  gsap.set(footerElements, {
    opacity: 0,
    y: 30,
    scale: 0.98,
  });

  // Animate elements with optimized stagger
  tl.to(footerElements, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: 0.8,
    stagger: {
      amount: 0.4,
      from: "start",
      ease: "power2.out",
    },
    ease: "power3.out",
  });

  // Play animation with a small delay to ensure DOM is ready
  setTimeout(() => {
    console.log("Playing footer animation");
    tl.play();
  }, 200);

  // Return cleanup function
  return () => {
    console.log("Cleaning up footer animations");
    tl.kill();
  };
};

/**
 * Enhanced social icon hover animations
 * @param element - The social icon element to animate
 */
export const animateSocialIcon = (element: HTMLElement) => {
  if (!element) return;

  // Create hover animations with better performance
  const hoverIn = () => {
    gsap.to(element, {
      scale: 1.15,
      rotation: 5,
      duration: 0.3,
      ease: "back.out(1.7)",
    });
  };

  const hoverOut = () => {
    gsap.to(element, {
      scale: 1,
      rotation: 0,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  // Add passive event listeners for better performance
  element.addEventListener("mouseenter", hoverIn, { passive: true });
  element.addEventListener("mouseleave", hoverOut, { passive: true });

  return () => {
    element.removeEventListener("mouseenter", hoverIn);
    element.removeEventListener("mouseleave", hoverOut);
  };
};

/**
 * Animates legal links with a subtle hover effect
 * @param element - The legal link element to animate
 */
export const animateLegalLink = (element: HTMLElement) => {
  if (!element) return;

  const hoverIn = () => {
    gsap.to(element, {
      scale: 1.05,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  const hoverOut = () => {
    gsap.to(element, {
      scale: 1,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  element.addEventListener("mouseenter", hoverIn, { passive: true });
  element.addEventListener("mouseleave", hoverOut, { passive: true });

  return () => {
    element.removeEventListener("mouseenter", hoverIn);
    element.removeEventListener("mouseleave", hoverOut);
  };
};

/**
 * Animates navigation links with staggered entrance
 * @param container - The navigation container
 */
export const animateNavigationLinks = (container: HTMLElement) => {
  if (!container) return;

  const links = container.querySelectorAll("a");

  if (!links.length) return;

  // Ensure links are visible first
  gsap.set(links, {
    opacity: 1,
    x: 0,
  });

  // Then animate from hidden state
  gsap.set(links, {
    opacity: 0,
    x: -20,
  });

  // Animate links in
  gsap.to(links, {
    opacity: 1,
    x: 0,
    duration: 0.5,
    stagger: 0.1,
    ease: "power2.out",
    delay: 0.3,
  });

  // Add hover effects to each link
  const cleanupFunctions = Array.from(links).map((link) => {
    const hoverIn = () => {
      gsap.to(link, {
        x: 5,
        duration: 0.2,
        ease: "power2.out",
      });
    };

    const hoverOut = () => {
      gsap.to(link, {
        x: 0,
        duration: 0.2,
        ease: "power2.out",
      });
    };

    link.addEventListener("mouseenter", hoverIn, { passive: true });
    link.addEventListener("mouseleave", hoverOut, { passive: true });

    return () => {
      link.removeEventListener("mouseenter", hoverIn);
      link.removeEventListener("mouseleave", hoverOut);
    };
  });

  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
};

/**
 * Animates contact info items with a subtle pulse effect
 * @param container - The contact info container
 */
export const animateContactInfo = (container: HTMLElement) => {
  if (!container) return;

  const contactItems = container.querySelectorAll("div");

  if (!contactItems.length) return;

  // Ensure items are visible first
  gsap.set(contactItems, {
    opacity: 1,
    y: 0,
  });

  // Then animate from hidden state
  gsap.set(contactItems, {
    opacity: 0,
    y: 15,
  });

  // Animate items in
  gsap.to(contactItems, {
    opacity: 1,
    y: 0,
    duration: 0.6,
    stagger: 0.15,
    ease: "power2.out",
    delay: 0.4,
  });
};

/**
 * Creates a subtle floating animation for logos
 * @param element - The logo element to animate
 */
export const createLogoFloatingAnimation = (element: HTMLElement) => {
  if (!element) return;

  const animation = gsap.to(element, {
    y: -5,
    duration: 3,
    repeat: -1,
    yoyo: true,
    ease: "sine.inOut",
    delay: Math.random() * 2, // Random delay for natural feel
  });

  return () => {
    animation.kill();
  };
};

/**
 * Cleanup all footer-specific ScrollTrigger instances
 */
export const cleanupFooterAnimations = () => {
  ScrollTrigger.getAll()
    .filter((trigger) => trigger.vars.id === "footer-animation")
    .forEach((trigger) => trigger.kill());
};
